import K from "utilities/constants";
import {
  BarChartOutlined,
  FileSearchOutlined,
  LineChartOutlined,
  TeamOutlined,
  StarOutlined,
} from "@ant-design/icons";
import {
  hasAtLeastOneAssignedClient,
  hasAtLeastOneClientWithEngagementMilestones,
} from "../routes/extraConditions";
import { FlowIcon } from "../common/icons/FlowIcon";
import { BillIcon } from "../common/icons/BillIcon";
import { BriefcaseAltIcon } from "../common/icons/BriefcaseAltIcon";
import { CalenderIcon } from "../common/icons/CalenderIcon";
import { ChartIcon } from "../common/icons/ChartIcon";
import { ClockIcon } from "../common/icons/ClockIcon";
import { CommissionApprovalIcon } from "../common/icons/CommissionApprovalIcon";
import { EngagementsIcon } from "../common/icons/EngagementsIcon";
import { FileContractDollarIcon } from "../common/icons/FileContractDollarIcon";
import { FilePercentageIcon } from "../common/icons/FilePercentageIcon";
import { FileUploadAltIcon } from "../common/icons/FileUploadAltIcon";
import { SettingIcon } from "../common/icons/SettingIcon";

const reportingChildren = (basePath) => [
  {
    path: `${basePath}/recruiter-performance`,
    name: "Recruiter Performance",
    icon: <TeamOutlined />,
    extraCondition: hasAtLeastOneAssignedClient,
    permission: [K.Permissions.ViewRecruiterPerformanceReporting],
  },
  {
    path: `${basePath}/client-reporting`,
    name: "Client Reporting",
    icon: <LineChartOutlined />,
    extraCondition: hasAtLeastOneAssignedClient,
    permission: [K.Permissions.ViewClientReporting],
  },
  {
    path: `${basePath}/internal-reporting`,
    name: "Internal Reporting",
    icon: <FileSearchOutlined />,
    extraCondition: hasAtLeastOneAssignedClient,
    permission: [K.Permissions.ViewInternalReporting],
  },
];

const billingCommissionsChildren = (basePath) => [
  {
    path: `${basePath}/submit`,
    name: "Submit to Billing",
    icon: <FileUploadAltIcon />,
    extraCondition: hasAtLeastOneAssignedClient,
    permission: [K.Permissions.ViewBillingApproval],
  },
  {
    path: `${basePath}/scheduled`,
    name: "Scheduled Billing",
    icon: <CalenderIcon />,
    extraCondition: hasAtLeastOneAssignedClient,
    permission: [K.Permissions.ViewScheduledBilling],
  },
  {
    path: `${basePath}/invoices`,
    name: "Invoices",
    icon: <FileContractDollarIcon />,
    extraCondition: hasAtLeastOneAssignedClient,
    permission: [K.Permissions.ViewInvoices],
  },
  {
    path: `${basePath}/commission-approval`,
    name: "Commission Approval",
    icon: <CommissionApprovalIcon />,
    extraCondition: hasAtLeastOneAssignedClient,
    permission: [K.Permissions.ViewCommissionApproval],
  },
  {
    path: `${basePath}/commission-statement`,
    name: "Commission Statements",
    icon: <FilePercentageIcon />,
    extraCondition: hasAtLeastOneAssignedClient,
    permission: [K.Permissions.ViewCommissionStatement],
  },
];
const configurationsChildren = (basePath) => [
  {
    path: `${basePath}/billing-clients`,
    name: "Billing - Clients",
    icon: <i className="icon-user" />,
    permission: [K.Permissions.ViewBillingClients],
  },
  {
    path: `${basePath}/billing-jobs`,
    name: "Billing - Jobs",
    icon: <BillIcon />,
    permission: [K.Permissions.ViewBillingJobs],
  },
  {
    path: `${basePath}/commission-plans`,
    name: "Commission Plans",
    icon: <i className="icon-commission-plans" />,
    permission: [K.Permissions.ViewCommissionPlan],
  },
  {
    path: `${basePath}/lookup-tables`,
    name: "Lookup Tables",
    icon: <i className="icon-lookup" />,
    permission: [K.Permissions.ViewLookupTable],
  },
  {
    path: `${basePath}/roles-permission`,
    name: "Roles & Permissions",
    icon: <i className="icon-roles-permissions" />,
    permission: [K.Permissions.ViewRolesAndPermissions],
  },
  {
    path: `${basePath}/users`,
    name: "Users",
    icon: <i className="icon-users" />,
    permission: [K.Permissions.ViewUsers],
  },
  {
    path: `${basePath}/client-contacts`,
    name: "Client Contacts",
    icon: <i className="icon-user-square" />,
    permission: [K.Permissions.ViewClientContacts],
  },
  {
    path: `${basePath}/reports`,
    name: "Reports",
    icon: <ChartIcon />,
    permission: [K.Permissions.ManageReports],
  },
  {
    path: `${basePath}/workflow`,
    name: "Candidate Workflow",
    icon: <FlowIcon />,
    permission: [K.Permissions.ViewCandidateWorkflow],
  },
  {
    path: `${basePath}/recruiter-goals`,
    name: "Recruiter Goals",
    icon: <StarOutlined />,
    permission: [K.Permissions.ViewRecruiterGoals],
  },
];

const navigations = [
  {
    name: "Dashboard",
    path: "/dashboard",
    icon: <i className="icon-dashboard" />,
    extraCondition: hasAtLeastOneAssignedClient,
    permission: [
      K.Permissions.ViewDashboardOwnProgress,
      K.Permissions.ViewDashboardTeamProgress,
    ],
  },
  {
    name: "Clients",
    path: "/clients",
    icon: <i className="icon-user" />,
    extraCondition: hasAtLeastOneAssignedClient,
  },
  {
    name: "Jobs",
    path: "/jobs",
    icon: <BriefcaseAltIcon />,
    extraCondition: hasAtLeastOneAssignedClient,
    permission: [K.Permissions.ViewJobs],
  },
  {
    name: "Candidates",
    path: "/candidates",
    icon: <i className="icon-candidate" />,
    extraCondition: hasAtLeastOneAssignedClient,
    permission: [K.Permissions.ViewCandidates],
  },
  {
    name: "Engagements",
    path: "/engagements",
    icon: <EngagementsIcon />,
    extraCondition: hasAtLeastOneClientWithEngagementMilestones,
    permission: [
      K.Permissions.ManageEngagementsForAssignedCandidates,
      K.Permissions.ManageEngagementsForAssignedClients,
    ],
  },
  {
    name: "Reporting",
    path: "/reporting",
    icon: <BarChartOutlined />,
    extraCondition: hasAtLeastOneAssignedClient,
    permission: [
      K.Permissions.ViewRecruiterPerformanceReporting,
      K.Permissions.ViewClientReporting,
      K.Permissions.ViewInternalReporting,
    ],
    children: reportingChildren("/reporting"),
  },
  {
    name: "Billings & Commissions",
    icon: <BillIcon />,
    extraCondition: hasAtLeastOneAssignedClient,
    permission: [
      K.Permissions.ViewBillingApproval,
      K.Permissions.ViewInvoices,
      K.Permissions.ViewCommissionApproval,
      K.Permissions.ViewCommissionStatement,
      K.Permissions.ViewScheduledBilling,
    ],
    children: billingCommissionsChildren("/billings"),
  },
  {
    name: "Configurations",
    icon: <SettingIcon />,
    permission: [
      K.Permissions.ViewBillingClients,
      K.Permissions.ViewBillingJobs,
      K.Permissions.ViewCommissionPlan,
      K.Permissions.ViewLookupTable,
      K.Permissions.ViewRolesAndPermissions,
      K.Permissions.ViewUsers,
      K.Permissions.ViewClientContacts,
      K.Permissions.ManageReports,
      K.Permissions.ViewCandidateWorkflow,
      K.Permissions.ViewRecruiterGoals,
    ],
    children: configurationsChildren("/configurations"),
  },
  {
    name: "Logs",
    path: "/logs",
    icon: <ClockIcon />,
    permission: [K.Permissions.ViewLogs],
  },
];

export default navigations;
